import React, { useState, useEffect } from 'react';
import { receiptScannerService } from '../../services/receiptScannerService';
import { CATEGORY_OPTIONS, normalizeCategoryId } from '../../lib/categories';

/**
 * Receipt Management Component
 * 
 * This component provides admin functionality for managing scanned receipts.
 * It allows viewing, editing, and deleting receipts, as well as batch operations.
 * 
 * @param {Object} props
 * @param {Function} props.onReceiptUpdated - Callback when receipt is updated
 * @param {Function} props.onError - Callback for error handling
 */
function ReceiptManagementComponent({ onReceiptUpdated, onError = () => {} }) {
  // State
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    category: '',
    minAmount: '',
    maxAmount: '',
    searchTerm: ''
  });
  
  // Load receipts on component mount
  useEffect(() => {
    loadReceipts();
  }, []);
  
  /**
   * Load receipts from the API
   */
  const loadReceipts = async () => {
    setLoading(true);
    
    try {
      // Prepare filter parameters
      const filterParams = {};
      
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;
      if (filters.category) filterParams.category = filters.category;
      if (filters.minAmount) filterParams.minAmount = parseFloat(filters.minAmount);
      if (filters.maxAmount) filterParams.maxAmount = parseFloat(filters.maxAmount);
      if (filters.searchTerm) filterParams.search = filters.searchTerm;
      
      // Fetch receipts
      const data = await receiptScannerService.getReceipts(filterParams);
      setReceipts(data);
    } catch (error) {
      console.error('Failed to load receipts:', error);
      onError('Failed to load receipts. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Handle filter changes
   */
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  /**
   * Apply filters
   */
  const applyFilters = () => {
    loadReceipts();
  };
  
  /**
   * Reset filters
   */
  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      category: '',
      minAmount: '',
      maxAmount: '',
      searchTerm: ''
    });
    
    // Load receipts without filters
    loadReceipts();
  };
  
  /**
   * View receipt details
   */
  const viewReceiptDetails = async (receiptId) => {
    try {
      const receipt = await receiptScannerService.getReceiptById(receiptId);
      setSelectedReceipt(receipt);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to load receipt details:', error);
      onError('Failed to load receipt details. Please try again.');
    }
  };
  
  /**
   * Start editing a receipt
   */
  const startEditing = () => {
    if (!selectedReceipt) return;
    
    setEditData({
      date: selectedReceipt.date || '',
      amount: selectedReceipt.amount || '',
      vendor: selectedReceipt.vendor || '',
      category: normalizeCategoryId(selectedReceipt.category || ''),
      notes: selectedReceipt.notes || ''
    });
    
    setIsEditing(true);
  };
  
  /**
   * Handle edit field changes
   */
  const handleEditChange = (field, value) => {
    const nextValue = field === 'category' ? normalizeCategoryId(value) : value;
    setEditData(prev => ({
      ...prev,
      [field]: nextValue
    }));
  };
  
  /**
   * Save receipt edits
   */
  const saveReceipt = async () => {
    if (!selectedReceipt || !isEditing) return;
    
    try {
      const updatedReceipt = await receiptScannerService.updateReceiptData(
        selectedReceipt.id,
        editData
      );
      
      // Update the receipt in the list
      setReceipts(prev => 
        prev.map(r => r.id === updatedReceipt.id ? updatedReceipt : r)
      );
      
      // Update selected receipt
      setSelectedReceipt(updatedReceipt);
      setIsEditing(false);
      
      // Notify parent component
      if (onReceiptUpdated) {
        onReceiptUpdated(updatedReceipt);
      }
      
    } catch (error) {
      console.error('Failed to update receipt:', error);
      onError('Failed to update receipt. Please try again.');
    }
  };
  
  /**
   * Cancel editing
   */
  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
  };
  
  /**
   * Delete a receipt
   */
  const deleteReceipt = async () => {
    if (!selectedReceipt) return;
    
    if (!window.confirm('Are you sure you want to delete this receipt? This action cannot be undone.')) {
      return;
    }
    
    try {
      await receiptScannerService.deleteReceipt(selectedReceipt.id);
      
      // Remove the receipt from the list
      setReceipts(prev => prev.filter(r => r.id !== selectedReceipt.id));
      
      // Clear selected receipt
      setSelectedReceipt(null);
      
    } catch (error) {
      console.error('Failed to delete receipt:', error);
      onError('Failed to delete receipt. Please try again.');
    }
  };
  
  /**
   * Close receipt details
   */
  const closeReceiptDetails = () => {
    setSelectedReceipt(null);
    setIsEditing(false);
    setEditData({});
  };
  
  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };
  
  /**
   * Format currency for display
   */
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  
  return (
    <div className="receipt-management bg-white rounded-lg shadow">
      {/* Filters Section */}
      <div className="p-6 border-b">
        <h2 className="text-xl font-semibold mb-4">Receipt Management</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <div className="flex space-x-2">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="From"
              />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="To"
              />
            </div>
          </div>
          
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Categories</option>
              <option value="Products">Products</option>
              <option value="Services">Services</option>
              <option value="Rent">Rent</option>
              <option value="Utilities">Utilities</option>
              <option value="Salaries">Salaries</option>
              <option value="Marketing">Marketing</option>
              <option value="Equipment">Equipment</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          {/* Amount Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount Range
            </label>
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.01"
                value={filters.minAmount}
                onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Min"
              />
              <input
                type="number"
                step="0.01"
                value={filters.maxAmount}
                onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Max"
              />
            </div>
          </div>
        </div>
        
        {/* Search and Buttons */}
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
          <input
            type="text"
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
            className="w-full sm:w-1/3 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Search by vendor or description"
          />
          
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={resetFilters}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
      
      {/* Receipts List */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vendor
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  Loading receipts...
                </td>
              </tr>
            ) : receipts.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  No receipts found. Try adjusting your filters.
                </td>
              </tr>
            ) : (
              receipts.map(receipt => (
                <tr 
                  key={receipt.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => viewReceiptDetails(receipt.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(receipt.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {receipt.vendor || 'Unknown Vendor'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(receipt.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {receipt.category || 'Uncategorized'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      receipt.status === 'processed' ? 'bg-green-100 text-green-800' :
                      receipt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {receipt.status || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        viewReceiptDetails(receipt.id);
                      }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Receipt Details Modal */}
      {selectedReceipt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium">
                Receipt Details
              </h3>
              <button
                type="button"
                onClick={closeReceiptDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Receipt Image */}
                <div>
                  {selectedReceipt.imageUrl ? (
                    <img 
                      src={selectedReceipt.imageUrl} 
                      alt="Receipt" 
                      className="w-full h-auto max-h-96 object-contain border rounded"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-200 flex items-center justify-center rounded">
                      <span className="text-gray-500">Receipt image not available</span>
                    </div>
                  )}
                </div>
                
                {/* Receipt Details */}
                <div>
                  {isEditing ? (
                    <div className="space-y-4">
                      {/* Edit Form */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date
                        </label>
                        <input
                          type="date"
                          value={editData.date}
                          onChange={(e) => handleEditChange('date', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Amount
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={editData.amount}
                          onChange={(e) => handleEditChange('amount', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Vendor
                        </label>
                        <input
                          type="text"
                          value={editData.vendor}
                          onChange={(e) => handleEditChange('vendor', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          value={editData.category}
                          onChange={(e) => handleEditChange('category', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Select a category</option>
                          {(CATEGORY_OPTIONS.expenses || []).map(({ id, name }) => (
                            <option key={id} value={id}>{name}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notes
                        </label>
                        <textarea
                          value={editData.notes}
                          onChange={(e) => handleEditChange('notes', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          rows="3"
                        ></textarea>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* View Details */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Date</h4>
                        <p>{formatDate(selectedReceipt.date)}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Amount</h4>
                        <p className="text-lg font-semibold">{formatCurrency(selectedReceipt.amount)}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Vendor</h4>
                        <p>{selectedReceipt.vendor || 'Unknown Vendor'}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Category</h4>
                        <p>{selectedReceipt.category || 'Uncategorized'}</p>
                      </div>
                      
                      {selectedReceipt.notes && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Notes</h4>
                          <p className="text-sm text-gray-600">{selectedReceipt.notes}</p>
                        </div>
                      )}
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Status</h4>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          selectedReceipt.status === 'processed' ? 'bg-green-100 text-green-800' :
                          selectedReceipt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedReceipt.status || 'Unknown'}
                        </span>
                      </div>
                      
                      {selectedReceipt.processedDate && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Processed Date</h4>
                          <p>{formatDate(selectedReceipt.processedDate)}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t flex justify-between">
              <div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={deleteReceipt}
                    className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete
                  </button>
                )}
              </div>
              
              <div className="flex space-x-3">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveReceipt}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={startEditing}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Edit Receipt
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceiptManagementComponent;
